/**
 * Storage utility for safe localStorage operations
 * Provides error handling and specific key management
 */
const APP_STORAGE_PREFIX = 'miroculus_';
const ENCRYPTION_KEY_STORAGE = `${APP_STORAGE_PREFIX}device_key`;
/**
 * Get item from localStorage with error handling
 * @param key - Storage key
 * @returns Stored value or null
 */
export const getItem = (key) => {
    try {
        return localStorage.getItem(key);
    }
    catch (error) {
        console.error('Storage getItem error:', error);
        return null;
    }
};
/**
 * Set item in localStorage with error handling
 * @param key - Storage key
 * @param value - Value to store
 * @returns Success status
 */
export const setItem = (key, value) => {
    try {
        localStorage.setItem(key, value);
        return true;
    }
    catch (error) {
        console.error('Storage setItem error:', error);
        return false;
    }
};
/**
 * Remove item from localStorage with error handling
 * @param key - Storage key
 * @returns Success status
 */
export const removeItem = (key) => {
    try {
        localStorage.removeItem(key);
        return true;
    }
    catch (error) {
        console.error('Storage removeItem error:', error);
        return false;
    }
};
/**
 * Clear app-specific keys from localStorage
 * Safer than clearing all localStorage which may affect other apps
 * @param keysToPreserve - Keys to keep (optional)
 * @returns Success status
 */
export const clearAppStorage = (keysToPreserve = []) => {
    try {
        const keysToRemove = [];
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && !keysToPreserve.includes(key)) {
                keysToRemove.push(key);
            }
        }
        keysToRemove.forEach((key) => localStorage.removeItem(key));
        return true;
    }
    catch (error) {
        console.error('Storage clearAppStorage error:', error);
        return false;
    }
};
/**
 * Clear all localStorage (use with caution)
 * @returns Success status
 */
export const clearAll = () => {
    try {
        localStorage.clear();
        return true;
    }
    catch (error) {
        console.error('Storage clearAll error:', error);
        return false;
    }
};
/**
 * Generate a cryptographically secure random string
 * @param length - Length of the string
 * @returns Random string
 */
const generateRandomString = (length = 32) => {
    const array = new Uint8Array(length);
    crypto.getRandomValues(array);
    return Array.from(array, (byte) => byte.toString(16).padStart(2, '0')).join('');
};
/**
 * Get or create a device-specific encryption key
 * Uses crypto.getRandomValues for secure key generation
 * Key is persisted in localStorage for consistency across sessions
 * @returns Device encryption key
 */
export const getEncryptionKey = () => {
    try {
        let key = localStorage.getItem(ENCRYPTION_KEY_STORAGE);
        if (!key) {
            key = generateRandomString(32);
            localStorage.setItem(ENCRYPTION_KEY_STORAGE, key);
        }
        return key;
    }
    catch (error) {
        console.error('Storage getEncryptionKey error:', error);
        // Fallback to session-based key if localStorage fails
        return generateRandomString(32);
    }
};
const storage = {
    getItem,
    setItem,
    removeItem,
    clearAppStorage,
    clearAll,
    getEncryptionKey,
};
export default storage;
